<template>
  <fieldset class="flex items-center justify-center gap-1 md:gap-4">
    <input
      v-for="i in 6"
      :key="i"
      name="code"
      type="number"
      class="h-16 w-12 rounded-lg text-center text-2xl code-input"
      :class="!customColorStyles && 'bg-slate-200'"
      :style="customColorStyles"
      inputmode="numeric"
      :readonly="locked"
      pattern="[0-9]*"
      :autocomplete="i === 1 && 'one-time-code'"
    />
  </fieldset>
</template>

<script setup>
import {onBeforeUnmount, onMounted} from "vue";

const props = defineProps({
  modelValue: {
    type: String,
    required: true,
  },
  focusOnMount: {
    type: Boolean,
    default: true,
  },
  focusDelay: {
    type: Number,
    default: 0,
  },
  locked: Boolean,
  customColorStyles: String,
});

const emits = defineEmits(["update:modelValue", "enter"]);

let inputElements = [];

const handleKeyDown = (index, e) => {
  if (e.keyCode === 8 && e.target.value === "")
    inputElements[Math.max(0, index - 1)].focus();
  if (e.keyCode === 13) emits("enter");
};

const handleInput = (index, e) => {
  const [first, ...rest] = e.target.value;
  e.target.value = first ?? "";
  const lastInputBox = index === inputElements.length - 1;
  const didInsertContent = first !== undefined;
  if (didInsertContent && !lastInputBox) {
    inputElements[index + 1].focus();
    inputElements[index + 1].value = rest.join("");
    inputElements[index + 1].dispatchEvent(new Event("input"));
  }
  setModelValue();
};

onMounted(() => {
  inputElements = [...document.querySelectorAll("input.code-input")];

  inputElements.forEach((ele, index) => {
    if (props.modelValue.length > index) {
      ele.value = props.modelValue[index];
    }
    if (index === 0 && props.focusOnMount) setTimeout(() => ele.focus(), props.focusDelay);
    ele.addEventListener("keydown", handleKeyDown.bind(null, index));
    ele.addEventListener("input", handleInput.bind(null, index));
  });
});

onBeforeUnmount(() => {
  inputElements.forEach((ele, index) => {
    ele.removeEventListener("keydown", handleKeyDown.bind(null, index));
    ele.removeEventListener("input", handleInput.bind(null, index));
  });
});

let lastEmit = "";

function setModelValue() {
  const code = inputElements.map(({ value }) => value).join("");
  if (code !== lastEmit) {
    emits("update:modelValue", code);
    lastEmit = code;
  }
}
</script>

<style>
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

input[type="number"] {
  -moz-appearance: textfield;
}
</style>
